import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ResetPasswordRequest } from "../@backend";
import { appState } from "../store";
import useApi from "../hooks/useApi";
import { FooterCmp } from "../ui/layout/Footer";
import { Menu } from "../ui/layout/Menu";
import { PageHeader } from "../ui/ds/PageHeader";
import { Input } from "../ui/ds/Input";
import { Btn } from "../ui/ds/Btn";

export const ResetPassword = () => {
    const { backend } = useApi(false);
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") ?? "";

    const [password1, setPassword1] = useState("");
    const [password2, setPassword2] = useState("");
    const [formErrors, setFormErrors] = useState([] as string[]);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFormErrors([]);
        setIsSuccess(false);

        if (!token) {
            setFormErrors(["Chybí token pro obnovu hesla."]);
            return;
        }

        if (password1.length <= 4) {
            setFormErrors(["Heslo musí mít alespoň 5 znaků."]);
            return;
        }

        if (password1 !== password2) {
            setFormErrors(["Hesla se neshodují."]);
            return;
        }

        setIsSubmitting(true);
        try {
            await backend.user.userControllerResetPassword({ token, password1, password2 } as ResetPasswordRequest);
            setIsSuccess(true);
            setPassword1("");
            setPassword2("");
        } catch (e: any) {
            setFormErrors([e?.body?.error?.message ?? e?.message ?? "Nepodařilo se nastavit nové heslo."]);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Menu />
            <PageHeader title="Obnova hesla" subtitle="Zadejte nové heslo pro váš účet" />

            <div className="max-w-xl mx-auto px-4 md:px-8 py-8">
                <form onSubmit={handleSubmit} className="bg-bg-card rounded-content shadow-card p-6 md:p-8 flex flex-col gap-5">

                    {isSuccess && (
                        <div className="flex flex-col rounded-card border border-semantic-success/30 bg-semantic-success/10 px-4 py-3" role="status">
                            <strong className="font-bold text-semantic-success font-nunito">Heslo bylo změněno.</strong>
                            <span className="tb-body mt-1">Nyní se můžete přihlásit novým heslem.</span>
                        </div>
                    )}

                    {formErrors.length > 0 && (
                        <div className="flex flex-col rounded-card border border-semantic-danger/30 bg-semantic-danger/10 px-4 py-3" role="alert">
                            <strong className="font-bold text-semantic-danger font-nunito">Heslo se nepodařilo změnit.</strong>
                            <span className="tb-body mt-1">{formErrors.join(", ")}</span>
                        </div>
                    )}

                    {!token && (
                        <div className="flex flex-col rounded-card border border-semantic-warning/30 bg-semantic-warning/10 px-4 py-3" role="alert">
                            <strong className="font-bold text-semantic-warning font-nunito">Neplatný odkaz.</strong>
                            <span className="tb-body mt-1">Odkaz pro obnovu hesla neobsahuje token.</span>
                        </div>
                    )}

                    {!isSuccess && (
                        <>
                            <Input
                                label="Nové heslo"
                                type="password"
                                name="password1"
                                id="reset-password-password1"
                                autoComplete="new-password"
                                required
                                disabled={!token || isSubmitting}
                                value={password1}
                                onChange={e => setPassword1(e.target.value)}
                                placeholder="Minimálně 5 znaků"
                            />
                            <Input
                                label="Nové heslo znovu"
                                type="password"
                                name="password2"
                                id="reset-password-password2"
                                autoComplete="new-password"
                                required
                                disabled={!token || isSubmitting}
                                value={password2}
                                onChange={e => setPassword2(e.target.value)}
                                placeholder="Zopakujte heslo"
                            />
                        </>
                    )}

                    <div className="flex flex-row flex-wrap gap-3 pt-1">
                        {isSuccess ? (
                            <Btn
                                variant="primary"
                                fullWidth
                                onClick={() => { appState.modals.showLoginModal = true; }}
                            >
                                Přihlásit se
                            </Btn>
                        ) : (
                            <>
                                <div className="flex-1">
                                    <Link to="/forgot-password">
                                        <Btn variant="ghost" fullWidth>Nový odkaz</Btn>
                                    </Link>
                                </div>
                                <div className="flex-1">
                                    <Btn type="submit" variant="primary" fullWidth disabled={!token || isSubmitting}>
                                        {isSubmitting ? "Ukládám…" : "Změnit heslo"}
                                    </Btn>
                                </div>
                            </>
                        )}
                    </div>
                </form>
            </div>

            <FooterCmp />
        </>
    );
};
